import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Center,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Logo } from '@fololabs/design/components';

interface Props {
  onSignIn: (email: string, password: string) => Promise<void>;
  /** Mock builds skip the password requirement and show a hint. */
  isMock?: boolean;
  /** Hosted UI — render a redirect button instead of the in-app form. */
  hostedUi?: boolean;
}

export function LoginPage({ onSignIn, isMock, hostedUi }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSignIn(email.trim(), password);
    } catch (err) {
      setError(
        err instanceof Error
          ? friendlyError(err.message)
          : 'Sign-in failed. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center
      style={{
        minHeight: '100dvh',
        backgroundColor: 'var(--mantine-color-mist-2)',
        padding: '1rem',
      }}
    >
      <Stack align="center" gap="lg" style={{ width: '100%', maxWidth: 380 }}>
        <Box style={{ color: 'var(--mantine-color-aquifer-7)' }}>
          <Logo size={40} />
        </Box>

        <Stack align="center" gap={4}>
          <Title order={2} c="aquifer.7" ta="center">
            Welcome to FoloLabs
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Sign in to access your products.
          </Text>
        </Stack>

        {hostedUi ? (
          <Card withBorder radius="md" p="lg" w="100%" shadow="sm">
            <Stack gap="md">
              {error && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconAlertTriangle size={16} />}
                  radius="md"
                  py="xs"
                >
                  {error}
                </Alert>
              )}
              <Button
                size="md"
                fullWidth
                color="aquifer"
                radius="md"
                loading={submitting}
                onClick={() => {
                  setError(null);
                  setSubmitting(true);
                  onSignIn('', '').catch((err) => {
                    setError(
                      err instanceof Error
                        ? friendlyError(err.message)
                        : 'Sign-in failed. Please try again.',
                    );
                    setSubmitting(false);
                  });
                }}
              >
                Sign in with FoloLabs SSO
              </Button>
            </Stack>
          </Card>
        ) : (
          <Card withBorder radius="md" p="lg" w="100%" shadow="sm">
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              {error && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconAlertTriangle size={16} />}
                  radius="md"
                  py="xs"
                >
                  {error}
                </Alert>
              )}

              <TextInput
                label="Email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                autoComplete="email"
                autoFocus
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required={!isMock}
                autoComplete="current-password"
              />

              <Button
                type="submit"
                size="md"
                fullWidth
                color="aquifer"
                radius="md"
                loading={submitting}
              >
                Sign in
              </Button>
            </Stack>
          </form>
          </Card>
        )}

        <Text size="xs" c="dimmed" ta="center">
          {isMock
            ? 'Mock mode — any email signs you in.'
            : 'Access is managed by your organisation administrator.'}
        </Text>
      </Stack>
    </Center>
  );
}

/** Map raw Amplify/Cognito errors to a user-facing line. */
function friendlyError(raw: string): string {
  const r = raw.toLowerCase();
  if (r.includes('incorrect username or password') || r.includes('notauthorized'))
    return 'Incorrect email or password.';
  if (r.includes('usernotfound') || r.includes('user does not exist'))
    return 'No account found for that email.';
  if (r.includes('network') || r.includes('failed to fetch'))
    return 'Network error — check your connection and try again.';
  if (r.includes('additional step'))
    return 'Your account needs extra setup. Contact your administrator.';
  return 'Sign-in failed. Please try again.';
}
